import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, GraduationCap, Users2, TrendingUp, Award, BookOpen, Edit3, UserPlus } from "lucide-react";

const CATEGORIES = [
  { v: "safety", l: "Safety" },
  { v: "sop", l: "SOP" },
  { v: "regulation", l: "Regulation" },
  { v: "technical", l: "Technical" },
  { v: "general", l: "General" },
];

export default function TrainingAdmin() {
  const [tab, setTab] = useState("dashboard"); // dashboard | courses | assignments
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [assignForm, setAssignForm] = useState({ assignee_ids: {}, due_date: "" });
  const [form, setForm] = useState(emptyCourse());

  function emptyCourse() {
    return { title: "", description: "", category: "safety", content: "", pass_score: 70, duration_minutes: 30, published: true,
      quiz: [{ question: "", options: ["", ""], correct_index: 0 }] };
  }

  const load = async () => {
    try {
      const [s, c, a, u] = await Promise.all([
        api.get("/training/stats"), api.get("/courses"),
        api.get("/course-assignments"), api.get("/users"),
      ]);
      setStats(s.data); setCourses(c.data); setAssignments(a.data); setUsers(u.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = async (c) => {
    try {
      const { data } = await api.get(`/courses/${c.id}`);
      setForm({ ...emptyCourse(), ...data, quiz: data.quiz?.length ? data.quiz : emptyCourse().quiz });
      setEditing(c.id); setCreateOpen(true);
    } catch { toast.error("Failed to load course"); }
  };

  const saveCourse = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const validQuiz = form.quiz.filter(q => q.question.trim() && q.options.every(o => o.trim()));
    const payload = { ...form, quiz: validQuiz, pass_score: Number(form.pass_score), duration_minutes: Number(form.duration_minutes) };
    try {
      if (editing) await api.put(`/courses/${editing}`, payload);
      else await api.post("/courses", payload);
      toast.success(editing ? "Course updated" : "Course created");
      setCreateOpen(false); setEditing(null); setForm(emptyCourse()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeCourse = async (id) => {
    if (!confirm("Delete this course and all its assignments?")) return;
    try { await api.delete(`/courses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAssign = async () => {
    const ids = Object.entries(assignForm.assignee_ids).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error("Select at least one user");
    try {
      const { data } = await api.post("/course-assignments", { course_id: assignFor.id, assignee_ids: ids, due_date: assignForm.due_date || null });
      toast.success(`Assigned to ${data.assigned} user(s)`);
      setAssignFor(null); setAssignForm({ assignee_ids: {}, due_date: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inspectors = users.filter(u => ["inspector", "admin", "approver"].includes(u.role));

  return (
    <div className="p-6 md:p-8" data-testid="training-admin-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Learning &amp; Development</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Training Administration</h1>
        <p className="text-sm text-gray-500 mt-1">Create courses, assign them to inspectors, and monitor completion and pass rates.</p>
      </div>

      <div className="flex items-center gap-1 mb-6">
        {[
          { k: "dashboard", label: "Dashboard" },
          { k: "courses", label: "Courses" },
          { k: "assignments", label: "Assignments" },
        ].map(({ k, label }) => (
          <button key={k} data-testid={`training-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold border transition-colors ${tab === k ? "bg-[#01ADEF] text-white border-[#01ADEF]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Total Courses" value={stats.kpis.total_courses} icon={BookOpen} accent="bg-[#01ADEF]" testid="tstat-courses" />
            <Kpi label="Total Documents" value={stats.kpis.total_documents} icon={GraduationCap} accent="bg-[#0284C7]" testid="tstat-docs" />
            <Kpi label="Pass Rate" value={`${stats.kpis.pass_rate_pct}%`} icon={TrendingUp} accent="bg-[#059669]" testid="tstat-passrate" />
            <Kpi label="Avg Score" value={`${stats.kpis.avg_score}%`} icon={Award} accent="bg-[#D97706]" testid="tstat-avg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
            <MiniStat label="Completed" value={stats.kpis.assignments_completed} tone="text-[#059669]" testid="tstat-completed" />
            <MiniStat label="In Progress" value={stats.kpis.assignments_in_progress} tone="text-[#D97706]" testid="tstat-inprogress" />
            <MiniStat label="Pending" value={stats.kpis.assignments_pending} tone="text-[#01ADEF]" testid="tstat-pending" />
            <MiniStat label="Failed" value={stats.kpis.assignments_failed} tone="text-[#DC2626]" testid="tstat-failed" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2"><Users2 className="w-4 h-4 text-[#01ADEF]" /><h2 className="font-display text-lg font-bold tracking-tight">Team Progress</h2></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Inspector</Th><Th>Completed</Th><Th>In Progress</Th><Th>Completion %</Th></tr></thead>
                <tbody data-testid="training-per-user">
                  {stats.per_user.length === 0 ? <tr><td colSpan={4} className="text-center py-8 text-gray-500">No training data yet.</td></tr> :
                    stats.per_user.map(u => (
                      <tr key={u.user_id} className="border-b border-gray-100" data-testid={`per-user-${u.user_id}`}>
                        <Td><div className="font-semibold">{u.name}</div><div className="text-[10px] uppercase tracking-wider text-gray-500">{u.role}</div></Td>
                        <Td className="font-mono">{u.completed} / {u.assigned}</Td>
                        <Td className="font-mono">{u.in_progress}</Td>
                        <Td><Bar pct={u.completion_pct} /></Td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#01ADEF]" /><h2 className="font-display text-lg font-bold tracking-tight">Course Adoption</h2></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Course</Th><Th>Assigned</Th><Th>Completed</Th><Th>Completion %</Th></tr></thead>
                <tbody data-testid="training-per-course">
                  {stats.per_course.length === 0 ? <tr><td colSpan={4} className="text-center py-8 text-gray-500">No course activity yet.</td></tr> :
                    stats.per_course.map(c => (
                      <tr key={c.course_id} className="border-b border-gray-100">
                        <Td className="font-semibold">{c.title}</Td>
                        <Td className="font-mono">{c.assigned}</Td>
                        <Td className="font-mono">{c.completed}</Td>
                        <Td><Bar pct={c.completion_pct} /></Td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "courses" && (
        <>
          <div className="flex items-center justify-end mb-3">
            <Button data-testid="new-course-btn" onClick={() => { setForm(emptyCourse()); setEditing(null); setCreateOpen(true); }} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" />New Course</Button>
          </div>
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Title</Th><Th>Category</Th><Th>Duration</Th><Th>Pass Mark</Th><Th>Quiz Qs</Th><Th>Published</Th><Th></Th></tr></thead>
              <tbody data-testid="courses-table">
                {courses.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">No courses yet.</td></tr> :
                  courses.map(c => (
                    <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`course-row-${c.id}`}>
                      <Td><div className="font-semibold">{c.title}</div><div className="text-xs text-gray-500 line-clamp-1 max-w-md">{c.description}</div></Td>
                      <Td className="capitalize">{c.category}</Td>
                      <Td className="font-mono">{c.duration_minutes}m</Td>
                      <Td className="font-mono">{c.pass_score}%</Td>
                      <Td className="font-mono">{(c.quiz || []).length}</Td>
                      <Td>{c.published ? <span className="text-xs font-bold px-2 py-1 border bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]">LIVE</span> : <span className="text-xs font-bold px-2 py-1 border bg-gray-100 text-gray-700 border-gray-300">DRAFT</span>}</Td>
                      <Td className="flex items-center gap-3">
                        <button data-testid={`assign-course-${c.id}`} onClick={() => setAssignFor(c)} className="text-[#01ADEF] hover:text-[#0198D6]" title="Assign"><UserPlus className="w-4 h-4" /></button>
                        <button onClick={() => openEdit(c)} className="text-gray-600 hover:text-gray-900" title="Edit"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => removeCourse(c.id)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                      </Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "assignments" && (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Course</Th><Th>Assignee</Th><Th>Assigned</Th><Th>Due</Th><Th>Status</Th><Th>Score</Th></tr></thead>
            <tbody data-testid="assignments-admin-table">
              {assignments.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-gray-500">No assignments yet.</td></tr> :
                assignments.map(a => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <Td className="font-semibold">{a.course_title}</Td>
                    <Td><div>{a.assignee_name}</div><div className="text-xs text-gray-500 font-mono">{a.assignee_email}</div></Td>
                    <Td className="font-mono text-xs">{a.assigned_at?.slice(0, 10)}</Td>
                    <Td className="font-mono text-xs">{a.due_date || "—"}</Td>
                    <Td><StatusPill s={a.status} /></Td>
                    <Td className="font-mono">{a.score != null ? `${a.score}%` : "—"}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Course create/edit */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEditing(null); setForm(emptyCourse()); } }}>
        <DialogContent className="rounded-md max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Course" : "New Course"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Title *"><Input data-testid="course-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-md" /></Field>
            <Field label="Description"><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-md" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Category"><Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Duration (min)"><Input type="number" min={1} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} className="rounded-md" /></Field>
              <Field label="Pass Mark (%)"><Input type="number" min={0} max={100} value={form.pass_score} onChange={e => setForm({ ...form, pass_score: e.target.value })} className="rounded-md" /></Field>
            </div>
            <Field label="Content (Markdown)"><Textarea data-testid="course-content-input" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write the lesson content here…" className="rounded-md min-h-[180px] font-mono text-xs" /></Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Quiz Questions</Label>
                <button type="button" onClick={() => setForm({ ...form, quiz: [...form.quiz, { _key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, question: "", options: ["", ""], correct_index: 0 }] })} className="text-xs uppercase tracking-wider font-bold text-[#01ADEF] hover:text-[#0198D6]"><Plus className="w-3 h-3 inline mr-1" />Add Question</button>
              </div>
              <div className="space-y-3">
                {form.quiz.map((q, qi) => (
                  <div key={q._key || `q-${qi}`} className="border border-gray-200 p-3" data-testid={`quiz-editor-${qi}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xs font-mono text-gray-500 mt-2">Q{qi + 1}</span>
                      <Input data-testid={`q-input-${qi}`} value={q.question} onChange={e => { const nx = [...form.quiz]; nx[qi] = { ...q, question: e.target.value }; setForm({ ...form, quiz: nx }); }} placeholder="Question…" className="rounded-md" />
                      <button type="button" onClick={() => setForm({ ...form, quiz: form.quiz.filter((_, i) => i !== qi) })} className="text-gray-400 hover:text-[#DC2626] mt-2"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {q.options.map((opt, oi) => (
                      <div key={`${q._key || qi}-opt-${oi}`} className="flex items-center gap-2 pl-6 mb-1">
                        <input type="radio" checked={q.correct_index === oi} onChange={() => { const nx = [...form.quiz]; nx[qi] = { ...q, correct_index: oi }; setForm({ ...form, quiz: nx }); }} title="Correct answer" />
                        <Input data-testid={`q-${qi}-opt-${oi}`} value={opt} onChange={e => { const nx = [...form.quiz]; const opts = [...q.options]; opts[oi] = e.target.value; nx[qi] = { ...q, options: opts }; setForm({ ...form, quiz: nx }); }} placeholder={`Option ${oi + 1}`} className="rounded-md" />
                        {q.options.length > 2 && <button type="button" onClick={() => { const nx = [...form.quiz]; const opts = q.options.filter((_, i) => i !== oi); nx[qi] = { ...q, options: opts, correct_index: Math.min(q.correct_index, opts.length - 1) }; setForm({ ...form, quiz: nx }); }} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    ))}
                    <button type="button" onClick={() => { const nx = [...form.quiz]; nx[qi] = { ...q, options: [...q.options, ""] }; setForm({ ...form, quiz: nx }); }} className="text-xs text-[#01ADEF] hover:text-[#0198D6] pl-6 mt-1">+ Add option</button>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.published} onCheckedChange={v => setForm({ ...form, published: !!v })} data-testid="course-published" />
              <span>Publish (make visible to inspectors)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="save-course-btn" onClick={saveCourse} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => { if (!o) { setAssignFor(null); setAssignForm({ assignee_ids: {}, due_date: "" }); } }}>
        <DialogContent className="rounded-md max-w-md">
          <DialogHeader><DialogTitle className="font-display">Assign &ldquo;{assignFor?.title}&rdquo;</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Due Date (optional)"><Input type="date" value={assignForm.due_date} onChange={e => setAssignForm({ ...assignForm, due_date: e.target.value })} className="rounded-md" /></Field>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Select users</Label>
              <div className="mt-2 border border-gray-200 max-h-[280px] overflow-y-auto" data-testid="assign-users-list">
                {inspectors.length === 0 ? <div className="p-4 text-sm text-gray-500 text-center">No users to assign.</div> :
                  inspectors.map(u => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                      <Checkbox data-testid={`assign-${u.id}`} checked={!!assignForm.assignee_ids[u.id]} onCheckedChange={v => setAssignForm({ ...assignForm, assignee_ids: { ...assignForm.assignee_ids, [u.id]: !!v } })} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.name}</div>
                        <div className="text-xs text-gray-500 font-mono truncate">{u.email}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border bg-gray-100 border-gray-300 text-gray-700">{u.role}</span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button data-testid="confirm-assign-btn" onClick={doAssign} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span><div className={`w-8 h-8 flex items-center justify-center ${accent}`}><Icon className="w-4 h-4 text-white" strokeWidth={2.5} /></div></div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
  </div>
);
const MiniStat = ({ label, value, tone, testid }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1">{label}</div>
    <div className={`font-display text-2xl font-bold tracking-tight ${tone}`}>{value}</div>
  </div>
);
const Bar = ({ pct }) => (
  <div className="flex items-center gap-2">
    <div className="w-24 h-2 bg-gray-100"><div className="h-2 bg-[#01ADEF]" style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
    <span className="font-mono text-xs">{pct || 0}%</span>
  </div>
);
const StatusPill = ({ s }) => {
  const map = {
    assigned: ["bg-[#E0F2FE]", "text-[#075985]", "border-[#7DD3FC]", "Assigned"],
    in_progress: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "In Progress"],
    completed: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Completed"],
    failed: ["bg-[#FEE2E2]", "text-[#B91C1C]", "border-[#FCA5A5]", "Failed"],
  };
  const [bg, tx, br, label] = map[s] || map.assigned;
  return <span className={`text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};
